import { IAction } from "../domain/action.interface";
import { IRoleAction, IUserRole, userService } from "../services/user.service";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MenuItem } from "./menu.store";
import { getIconOrDefault } from "../utils/menuConfig";
import {
	CUPS_ANALYTES_PATH,
	LEGACY_ANALYTES_PATH,
} from "../pages/CupsAndAnalytes/tabs";

/** A retired route is authorised as the route that replaced it (SIMS-8794). */
export const retiredRouteTarget = (path: string): string => {
	const first = `/${(path ?? "").split("/")[1] ?? ""}`.toLowerCase();
	return first === LEGACY_ANALYTES_PATH ? CUPS_ANALYTES_PATH : path;
};

/**
 * SIMS-8717 AC2 — nesting is CONFIGURATION; visibility still follows the GRANT.
 *
 * The SERVER applies the grant. `GET /users/user-info` (backend `processRoleData`) replaces each
 * parent's `subActions` with only the sub-actions the same role grants, in their configured order,
 * and removes those children from the top level of `role.actions`. So the nested list IS the
 * visible list, and a granted child arrives only there.
 *
 * SIMS-9321: daa0f6f5 filtered the nested children again, against the top-level ids. Those no
 * longer hold the children the server nested, so every parent lost all of them and became a link
 * ("Reports Center" stopped expanding). The children are now taken as sent, never re-filtered.
 */
const actionToMenu = (action: IAction): MenuItem => {
	// Only populated entries can render: a raw id string (an older record, or a level the server did
	// not populate) has no title, icon or route, and handing it to actionToMenu would build a broken
	// entry. Dropping them keeps the order of the rest.
	// Typed IAction[], but the wire can carry strings, hence the widened element type.
	const children = (
		(action.subActions ?? []) as Array<IAction | string>
	).filter((sub): sub is IAction => typeof sub !== "string");
	return {
		id: action._id,
		icon: action.icon || getIconOrDefault(action.code),
		title: action.name,
		route: action.link,
		// `parent` vs `link` follows what the user can actually SEE: a parent with nothing to show
		// is a plain link, not an expander that opens onto nothing.
		type: children.length > 0 ? "parent" : "link",
		open: false,
		active: false,
		children: children.map((sub) => actionToMenu(sub)),
	};
};

/**
 * Derive the sidebar menu items from a user's granted role actions.
 *
 * SIMS-7884: extracted so the login-time fetch (`getUserByEmail`) and the
 * silent mid-session revalidation (`refreshUserInfo`) cannot drift apart — a
 * refresh must rebuild exactly the same menu the login built.
 */
const deriveMenus = (user: IUserRole): MenuItem[] => {
	const allActions = user.roles.flatMap((role) => role.actions);
	// Users granted the B2D dashboard hide the legacy "Dashboard"
	// menu so they only see their entry point.
	const hasB2DDashboard = allActions.some((a) => a.code === "DASH_VIEW_B2D");
	// Users granted the consolidated Reports hide the legacy "Reports"
	// parent so they only see the new entry.
	const hasConsolidatedReports = allActions.some(
		(a) => a.code === "REP_CONS_VIEW",
	);

	return allActions
		.filter((action) => action.type === "MENU_OPTION")
		.filter((action) => !(hasB2DDashboard && action.code === "DASH_VIEW"))
		.filter((action) => !(hasConsolidatedReports && action.code === "REP_VIEW"))
		.sort((a, b) => a.order - b.order)
		.map((action) => actionToMenu(action));
};

// Define the interface for the store's state
export interface UserStore {
	userInfo: IUserRole | null;
	lastFetchedAt: number | null;
	userHasAccess: (actionUrl: string) => boolean;
	userHasAccessToCode: (roleCode: string) => boolean;
	menus: MenuItem[];
	roles: IRoleAction[];
	getUserByEmail: (email: string, subIdp: string) => Promise<IUserRole>;
	refreshUserInfo: (email: string, subIdp: string) => Promise<IUserRole>;
	clearUser: () => void;
	isLoading: boolean;
}

// Create the store
export const useUserStore = create<UserStore>()(
	persist(
		(set, get) => ({
			userInfo: null,
			lastFetchedAt: null,
			menus: [],
			roles: [],
			isLoading: false,
			userHasAccess: (requested: string) => {
				//everyone has access to unauthorized page or home
				if (["/unauthorized", "/"].includes(requested?.toLowerCase()))
					return true;
				// SIMS-8794 — the retired /analytes route redirects to the merged screen, so it is
				// judged as /cups. Otherwise this guard runs before the redirect and refuses a user
				// whose roles were migrated onto the cups action.
				const path = retiredRouteTarget(requested);

				const user = get().userInfo;
				if (!user) return false;

				return user.roles.some((role) =>
					role.actions.some((a) => {
						const pathIsValid = path.split("/").length > 1;
						const pathSecondPart = path.split("/")[1].toLowerCase();

						const userHasAccess = pathIsValid
							? a.link?.toLowerCase().includes(pathSecondPart)
							: false;
						const userHasChildAccess = a.subActions?.some((subAction) =>
							subAction.link.toLowerCase().includes(pathSecondPart),
						);
						return userHasAccess || userHasChildAccess;
					}),
				);
			},
			userHasAccessToCode: (roleCode: string) => {
				const user = get().userInfo;
				if (!user) return false;

				return user.roles.some((role) =>
					role.actions.some((a) => {
						const userHasAccess = a.code === roleCode;
						const userHasChildAccess = a.subActions?.some(
							(subAction) => subAction.code === roleCode,
						);
						return userHasAccess || userHasChildAccess;
					}),
				);
			},
			getUserByEmail: async (email: string, subIdp: string) => {
				set({ isLoading: true });
				try {
					const user = await userService.findByEmail(email, subIdp);

					set({
						userInfo: user,
						lastFetchedAt: Date.now(),
						menus: deriveMenus(user),
						roles: user.roles,
						isLoading: false,
					});
					return user;
				} catch (error) {
					set({ isLoading: false });
					throw error;
				}
			},
			// SIMS-7884: silent mid-session re-read of the logged-in user.
			//
			// `userInfo` used to be written exactly once — by `getUserByEmail`
			// during App bootstrap — and then persisted to localStorage. Because
			// ~30 screens scope themselves by `userInfo.programs`, a program
			// assigned to the user mid-session stayed invisible everywhere until
			// they signed out and back in (which is what finally cleared the
			// snapshot). This action makes that snapshot refreshable.
			//
			// Deliberately does NOT touch `isLoading`: App.tsx gates the
			// full-screen spinner on it (and Callback reads it too), so flipping
			// it during a background revalidation would blank the app mid-session.
			//
			// On failure the previous snapshot is left untouched and the error is
			// re-thrown for the caller (React Query) to record — a revalidation
			// that cannot reach the backend must never downgrade a working session.
			refreshUserInfo: async (email: string, subIdp: string) => {
				const user = await userService.findByEmail(email, subIdp);

				set({
					userInfo: user,
					lastFetchedAt: Date.now(),
					menus: deriveMenus(user),
					roles: user.roles,
				});
				return user;
			},
			clearUser: () =>
				set({ userInfo: null, lastFetchedAt: null, menus: [], roles: [] }),
		}),
		{
			name: "UserStore",
		},
	),
);
